import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ArrowDownward, ArrowUpward, Close } from "@mui/icons-material";
import { DialogTitleClose, PaperComponent } from "../dialog";

const emptySlot = () => ({
  name: "",
  code: "",
  details: [],
  certificationIds: [],
});

const CERT_GROUPS = [
  { division: "RAW_MATERIAL", label: "원자재" },
  { division: "SUB_MATERIAL_PROCESS", label: "가공부자재" },
  { division: "SUB_MATERIAL_BUILD", label: "시공부자재" },
];

const GMaterialSlotDialog = ({
  open,
  modify,
  slot,
  components,
  certifications,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(emptySlot());
  const [slotInput, setSlotInput] = useState("");
  const [division, setDivision] = useState(CERT_GROUPS[0].division);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setError("");
    setSlotInput("");
    const certificationIds = slot
      ? (slot.certificationIds || (slot.certifications || []).map((cert) => cert.g04docuGCertificationId))
      : [];
    const savedDivision = CERT_GROUPS.find((group) => (
      (certifications || []).some((cert) => certificationIds.includes(cert.id) && cert.division === group.division)
      || (slot?.certifications || []).some((cert) => cert.division === group.division)
    ))?.division;
    setDivision(savedDivision || CERT_GROUPS[0].division);
    if (slot) {
      setForm({
        id: slot.id,
        name: slot.name || "",
        code: slot.code || "",
        details: (slot.details || []).map((detail) => ({
          gcomponentId: detail.gcomponentId,
          gcomponentCode: detail.gcomponentCode,
          gcomponentName: detail.gcomponentName,
        })),
        certificationIds,
      });
    } else {
      setForm(emptySlot());
    }
  }, [open, slot, certifications]);

  const componentOptions = useMemo(() => {
    const selected = new Set(form.details.map((detail) => detail.gcomponentId));
    return (components || [])
      .filter((component) => component.id && component.code && !selected.has(component.id))
      .slice()
      .sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [components, form.details]);

  const visibleCerts = useMemo(() => (
    (certifications || []).filter((cert) => cert.division === division)
  ), [certifications, division]);

  const toggleCertification = (certId, checked) => {
    const ids = checked
      ? form.certificationIds.concat(certId)
      : form.certificationIds.filter((id) => id !== certId);
    setForm({ ...form, certificationIds: ids });
  };

  const moveDetail = (index, direction) => {
    const next = form.details.slice();
    const target = index + direction;
    if (target < 0 || target >= next.length) {
      return;
    }
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setForm({ ...form, details: next });
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      setError("자재품종 이름을 입력하세요.");
      return;
    }
    if (form.details.length === 0) {
      setError("칸을 하나 이상 선택하세요.");
      return;
    }
    if (form.certificationIds.length === 0) {
      setError("인증규격을 하나 이상 선택하세요.");
      return;
    }
    setError("");
    onSave({
      id: form.id,
      name: form.name.trim(),
      code: form.code.trim() || null,
      details: form.details.map((detail) => ({ gcomponentId: detail.gcomponentId })),
      certificationIds: form.certificationIds,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitleClose
        id="draggable-dialog-title"
        onClose={onClose}
        style={{ cursor: "move" }}
      >
        {modify ? "자재품종 수정" : "자재품종 등록"}
      </DialogTitleClose>
      <DialogContent>
        {error ? <ul><li>{error}</li></ul> : null}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="이름"
              size="small"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="코드"
              size="small"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ mt: 2, mb: 1 }} variant="h6">칸 순서</Typography>
            <Autocomplete
              size="small"
              options={componentOptions}
              getOptionLabel={(option) => `${option.name || ""} (${option.code || ""})`}
              value={null}
              inputValue={slotInput}
              onInputChange={(_event, value, reason) => {
                setSlotInput(reason === "input" ? value : "");
              }}
              clearOnBlur
              onChange={(_event, option) => {
                if (!option) {
                  return;
                }
                setSlotInput("");
                setForm({
                  ...form,
                  details: form.details.concat({
                    gcomponentId: option.id,
                    gcomponentCode: option.code,
                    gcomponentName: option.name,
                  }),
                });
              }}
              renderInput={(params) => <TextField {...params} size="small" label="칸 추가" placeholder="코드 또는 이름" />}
            />
            {form.details.map((detail, index) => (
              <Box key={detail.gcomponentId} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Typography sx={{ flex: 1 }} variant="body2">{index + 1}. {detail.gcomponentName} ({detail.gcomponentCode})</Typography>
                <IconButton size="small" onClick={() => moveDetail(index, -1)} aria-label="위로"><ArrowUpward fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => moveDetail(index, 1)} aria-label="아래로"><ArrowDownward fontSize="small" /></IconButton>
                <IconButton
                  size="small"
                  aria-label="빼기"
                  onClick={() => setForm({ ...form, details: form.details.filter((item) => item.gcomponentId !== detail.gcomponentId) })}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ mt: 1, mb: 1 }} variant="h6">인증규격</Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={division}
              onChange={(_event, next) => {
                if (next) {
                  setDivision(next);
                }
              }}
            >
              {CERT_GROUPS.map((group) => (
                <ToggleButton key={group.division} value={group.division} sx={{ fontWeight: "bold" }}>
                  {group.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ mt: 1, maxHeight: 280, overflow: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1 }}>
              {visibleCerts.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ gridColumn: "1 / -1" }}>해당하는 인증규격이 없습니다.</Typography>
              ) : visibleCerts.map((cert) => (
                <FormControlLabel
                  key={cert.id}
                  sx={{
                    display: "flex",
                    ml: 0,
                    mr: 0,
                    alignItems: "center",
                    "& .MuiCheckbox-root": { p: "4px" },
                    "& .MuiFormControlLabel-label": { fontSize: "0.875rem", lineHeight: 1.4 },
                  }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={form.certificationIds.includes(cert.id)}
                      onChange={(e) => toggleCertification(cert.id, e.target.checked)}
                    />
                  )}
                  label={cert.name || ""}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave}>{modify ? "수정" : "저장"}</Button>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GMaterialSlotDialog;
